<template>
  <div class="data-table-container">
    <table class="data-table" :class="{ striped: stripe }">
      <thead v-if="showHeader">
        <tr>
          <th 
            v-for="column in columns" 
            :key="column.name"
            :style="{ width: column.width ? column.width + 'px' : 'auto' }"
            class="table-header-cell"
          >
            {{ column.label }}
          </th>
          <th v-if="hasActions" class="table-header-cell">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr 
          v-for="(item, index) in data" 
          :key="index"
          class="table-row"
          :class="{ 
            striped: stripe && index % 2 === 1,
            active: index === activeRow
          }"
        >
          <td 
            v-for="column in columns" 
            :key="column.name"
            class="table-cell"
            @click="handleCellClick(item, index, column)"
          >
            <!-- 图片类型 -->
            <img 
              v-if="column.type === 'img'" 
              :src="item[column.name]" 
              class="cell-image"
              @error="handleImageError"
            />
            <!-- 普通文本 -->
            <span v-else>
              {{ item[column.name] || column.emptyString || '--' }}
            </span>
          </td>
          <td v-if="hasActions" class="table-cell actions-cell">
            <button 
              v-if="showEditButton"
              @click="handleEdit(item, index)"
              class="action-btn edit-btn"
            >
              编辑
            </button>
            <button 
              v-if="showDeleteButton"
              @click="handleDelete(item, index)"
              class="action-btn delete-btn"
            >
              删除
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    
    <!-- 空数据提示 -->
    <div v-if="!data || data.length === 0" class="empty-data">
      暂无数据
    </div>
  </div>
</template>

<script>
export default {
  name: 'DataTable',
  props: {
    // 表格列配置
    columns: {
      type: Array,
      default: () => []
    },
    // 表格数据
    data: {
      type: Array,
      default: () => []
    },
    // 是否显示表头
    showHeader: {
      type: Boolean,
      default: true
    },
    // 是否显示斑马纹
    stripe: {
      type: Boolean,
      default: false
    },
    // 是否显示边框
    border: {
      type: Boolean,
      default: false
    },
    // 是否显示编辑按钮
    showEditButton: {
      type: Boolean,
      default: true
    },
    // 是否显示删除按钮
    showDeleteButton: {
      type: Boolean,
      default: true
    },
    // 当前选中的行索引
    activeRow: {
      type: Number,
      default: -1
    }
  },
  computed: {
    // 是否有操作列
    hasActions() {
      return this.showEditButton || this.showDeleteButton;
    }
  },
  methods: {
    // 处理单元格点击
    handleCellClick(item, index, column) {
      this.$emit('cell-click', item, index, column);
    },
    
    // 处理编辑
    handleEdit(item, index) {
      this.$emit('edit', item, index);
    },
    
    // 处理删除
    handleDelete(item, index) {
      this.$emit('delete', item, index);
    },
    
    // 处理图片加载错误
    handleImageError(e) {
      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuaXoOinhumikeeUn+aIkDwvdGV4dD48L3N2Zz4=';
    },
    
    // 设置活动行
    setActiveRow(index) {
      this.$emit('update:active-row', index);
    }
  }
}
</script>

<style scoped>
.data-table-container {
  width: 100%;
  overflow: auto;
  border-radius: 15px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  background-color: white;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table.border {
  border: 1px solid #ebeef5;
}

.table-header-cell {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  color: #6a5acd;
  background-color: #f0f0ff;
  border-bottom: 2px solid #e8e8ff;
}

.data-table.border .table-header-cell {
  border-right: 1px solid #e8e8ff;
}

.data-table.border .table-header-cell:last-child {
  border-right: none;
}

.table-row {
  transition: background-color 0.25s ease;
}

.table-row:hover {
  background-color: #f9f9ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(106, 90, 205, 0.1);
}

.table-row.striped {
  background-color: #f9f9ff;
}

.table-row.striped:hover {
  background-color: #f0f0ff;
}

.table-row.active {
  border: 2px solid #f44336;
  background-color: rgba(244, 67, 54, 0.05);
}

.table-row.active .table-cell {
  color: #d32f2f;
  font-weight: 500;
}

.table-cell {
  padding: 10px 12px;
  color: #555;
  border-bottom: 1px solid #e8e8ff;
  cursor: pointer;
  transition: all 0.3s;
}

.data-table.border .table-cell {
  border-right: 1px solid #e8e8ff;
}

.data-table.border .table-cell:last-child {
  border-right: none;
}

.cell-image {
  width: 50px;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e8e8ff;
  transition: all 0.3s;
}

.cell-image:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(106, 90, 205, 0.2);
}

.actions-cell {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 5px 10px;
  font-size: 12px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.edit-btn {
  background-color: #6a5acd;
  color: white;
}

.edit-btn:hover {
  background-color: #5544b8;
  transform: translateY(-1px);
}

.delete-btn {
  background-color: #ff9eb5;
  color: white;
}

.delete-btn:hover {
  background-color: #ff7aa3;
  transform: translateY(-1px);
}

.empty-data {
  padding: 20px;
  text-align: center;
  color: #6a5acd;
  font-size: 14px;
  font-weight: 500;
}
</style>